import {Navigate, useNavigate, useSearchParams} from "react-router-dom";
import {SearchParam} from "@/helpers/search_params";
import {useCallback, useContext, useEffect, useMemo, useRef, useState} from "react";
import {CarecoApiContext} from "@/app-context/careco-api-context";
import {useActiveRoster} from "@/store/roster_store";
import {useAuthStore} from "@/store/auth_store";
import {useQuery, useQueryClient} from "@tanstack/react-query";
import TabsComponent, {TabsRef} from "@common/TabsComponent";
import TaskEditProgress, {TaskEditProgressRef} from "@/components/pages/tasks/task_edit/progress/TaskEditProgress";
import {MessageThreadSummary} from "@/api/dto/message";
import {useMixpanel} from "@/hooks/useMixpanel";
import {useFeatureToggles} from "@/hooks/useFeatureToggles";
import {FeatureToggle, GroupType} from "@/api/dto/dto";
import PermissionsHelpers from "@/helpers/permissions_helpers";
import {
  DataPermissionLevel,
  DataPermissionLevelNew,
  DataSource,
  Feature,
  FeatureNew,
  FeaturePermissionLevel
} from "@/api/dto/permissions";
import {MixpanelEventType, MixpanelTaskEvent} from "@/api/dto/mixpanel";
import Loading from "@common/Loading";
import {
  Content,
  Main,
  Messages,
  MessagesTitle,
  TabsWrapper
} from "@/components/pages/tasks/task_detail/TaskDetailPage.styles";
import MessageThreadDrawer from "@clients/profile_page/messages_tab/MessageThreadDrawer";
import ClientDemographics from "@clients/profile_page/ClientDemographics";
import {css} from "@emotion/css";
import {sizes} from "patient-ping-remedy/packages/theme";
import TaskEditLog from "@/components/pages/tasks/task_edit/log/TaskEditLog";
import ConfirmCloseModal from "@/components/pages/tasks/task_edit/ConfirmCloseModal";
import {usePageHeaderContent, usePageTitle, useBreadcrumbTitle} from "@/hooks/useBreadcrumbs";
import useJourneyRoster from "@/hooks/useJourneyRoster";
import Popover from "patient-ping-remedy/packages/popover";
import {Menu, MenuItem, MenuItemTypography} from "patient-ping-remedy/packages/menu";
import Button from "patient-ping-remedy/packages/button";
import {
  CareJourneyDetailsResponse,
  taskStatusDisplayValues,
  TaskStepName,
  taskTypeDisplayValues
} from "@/api/dto/care-journey";
import Icon, {ICONS} from "patient-ping-remedy/packages/icon";
import {
  appointmentTypes
} from "@/components/pages/tasks/task_edit/progress/bh_journey/ScheduleAppointmentsStep.constants";
import RawTaskJson from "@/components/pages/tasks/task_edit/RawTaskJson";
import {cloneDeep} from "lodash-es";

const getAppointmentJourneyDetails = (appointmentId?: string | null, careJourneyDetails?: CareJourneyDetailsResponse) => {
  const appointmentJourneyDetails = careJourneyDetails?.steps
    .find(step => step.stepName === TaskStepName.SCHEDULED_APPOINTMENTS)?.
    appointments?.find(appointment => appointment.careJourneyInstanceId === appointmentId);

  if(appointmentJourneyDetails && careJourneyDetails) {
    const copiedJourney = cloneDeep(appointmentJourneyDetails);
    copiedJourney.clientProfile = cloneDeep(careJourneyDetails.clientProfile);
    return copiedJourney;
  }

  return appointmentJourneyDetails;
};

const BhAppointmentDetailPage = () => {
  const [searchParams] = useSearchParams();
  const taskId = searchParams.get(SearchParam.TASK_ID);
  const appointmentId = searchParams.get(SearchParam.APPOINTMENT_ID);
  const navigate = useNavigate();
  const {checkBooleanToggle} = useFeatureToggles();
  const {carecoApi} = useContext(CarecoApiContext);
  const currentRoster = useActiveRoster();
  const {currentUser} = useAuthStore();
  const taskEditProgressRef = useRef<TaskEditProgressRef>(null);
  const queryClient = useQueryClient();
  const tabsRef = useRef<TabsRef>(null);
  const [newMessageThread, setNewMessageThread] = useState<MessageThreadSummary>();
  const [showConfirmClose, setShowConfirmClose] = useState(false);
  const [showPopover, setShowPopover] = useState(false);
  const {mixpanelEventProperties, getCurrentGroupDetails} = useMixpanel();

  useJourneyRoster();

  const isNewPermissionsEnabled = checkBooleanToggle(FeatureToggle.CARECO_3415_NEW_USER_PERMISSIONS);

  const isDemographicEditAvailable = !!currentUser &&
      !!currentRoster?.admittingFacility &&
      !currentRoster.automated &&
      currentRoster.type !== GroupType.CRISIS &&
      (isNewPermissionsEnabled ?
        PermissionsHelpers.canForUserDataPermissionRosterNew(
          currentUser,
          currentRoster,
          DataPermissionLevelNew.ACT,
          DataSource.ADT,
        ) : PermissionsHelpers.canForUserDataPermissionRoster(
          currentUser,
          currentRoster,
          DataPermissionLevel.WRITE,
          DataSource.ADT,
        ));

  const canActOnTaskListForRoster = currentUser && currentRoster && (isNewPermissionsEnabled ?
    PermissionsHelpers.canForUserFeaturePermissionRosterNew(
      currentUser,
      currentRoster,
      FeaturePermissionLevel.ACT,
      FeatureNew.TASKS,
    ) : PermissionsHelpers.canForUserFeaturePermissionRoster(
      currentUser,
      currentRoster,
      FeaturePermissionLevel.ACT,
      Feature.TASKS,
    ));

  const {data: careJourneyDetails, isLoading, isFetching} = useQuery({
    queryKey: ['careJourneyDetails', taskId],
    queryFn: () => carecoApi?.getCareJourney(taskId!, currentRoster!.id),
    enabled: !!carecoApi && !!taskId && !!currentRoster,
  });

  const appointmentJourneyDetails = useMemo(
    () => getAppointmentJourneyDetails(appointmentId, careJourneyDetails),
    [appointmentId, careJourneyDetails]
  );

  const {data: messageThreadSummaries, isFetching: isFetchingMessages} = useQuery({
    queryKey: ['messageThreadSummaries', currentRoster?.id,
      careJourneyDetails?.clientProfile.patient.patientId,
      appointmentJourneyDetails?.careJourneyInstanceId],
    queryFn: () => carecoApi?.getMessageThreadSummaries(
        currentRoster!.id,
        careJourneyDetails!.clientProfile.patient.patientId,
        appointmentJourneyDetails!.careJourneyInstanceId,
        null,
        null
    ),
    staleTime: 0,
    refetchOnWindowFocus: false,
    enabled: !!carecoApi && !!careJourneyDetails && !!currentRoster && !!appointmentJourneyDetails,
  });

  const taskType = careJourneyDetails ? taskTypeDisplayValues[careJourneyDetails.eventType] : undefined;
  const appointmentType = appointmentJourneyDetails
    ? appointmentTypes[appointmentJourneyDetails.providerType] ?? appointmentJourneyDetails.journeyType
    : undefined;

  const taskDetailsPageUrl = `/tasks/details?${SearchParam.TASK_ID}=${taskId}&${SearchParam.ROSTER_ID}=${currentRoster?.id}`;

  useBreadcrumbTitle('/tasks/details', taskType, taskDetailsPageUrl);
  usePageTitle(appointmentType);

  const navigateBack = useCallback(() => {
    navigate(taskDetailsPageUrl);
  }, [navigate, taskDetailsPageUrl]);

  const invalidate = useCallback(() => {
    void queryClient.refetchQueries({queryKey: ['careJourneyDetails', taskId]});
    void queryClient.removeQueries({queryKey: ['careJourneysData', currentRoster?.id]});
  }, [currentRoster, queryClient, taskId]);

  useEffect(() => {
    if (appointmentJourneyDetails && careJourneyDetails && currentRoster && mixpanelEventProperties) {
      const event: MixpanelTaskEvent = {
        eventName: MixpanelEventType.BIH_TASK_VIEWED,
        eventType: careJourneyDetails.eventType,
        careJourneyName: appointmentJourneyDetails.journeyType,
        ...mixpanelEventProperties!,
        ...getCurrentGroupDetails(currentRoster)
      };
      carecoApi?.postMixpanelEvent(event);
    }
  }, [mixpanelEventProperties, careJourneyDetails, currentRoster]);

  usePageHeaderContent(careJourneyDetails && (
    <Popover
      toggle={() => setShowPopover(!showPopover)}
      isOpen={showPopover}
      content={
        <Menu
          onHide={() => setShowPopover(false)}
          marginTop
          marginBottom
        >
          <MenuItem
            onClick={() => {
              setShowConfirmClose(true);
              setShowPopover(false);
            }}
          >
            <MenuItemTypography>Close</MenuItemTypography>
          </MenuItem>
        </Menu>
      }
    >
      <Button onClick={() => {}} disabled={appointmentJourneyDetails?.status?.toLowerCase() === 'closed'
                || appointmentJourneyDetails?.status?.toLowerCase() === 'cancelled'}>
        Appointment: {appointmentJourneyDetails?.status ? taskStatusDisplayValues[appointmentJourneyDetails.status] ?? '' : ''}
        <Icon
          iconClass={ICONS['angle-down']}
          iconSize="lg"
          weight="far"
          className={css({marginLeft: sizes.xxSmall})}
        />
      </Button>
    </Popover>
  ));

  const setCurrentTab = (index: number, stepName: string) => {
    tabsRef.current?.changeTab(index);
    setTimeout(() => taskEditProgressRef.current?.changeTab(appointmentJourneyDetails?.steps
      .findIndex((step) => step.stepName === stepName) ?? -1));
  };

  if (!taskId) return <Navigate to="/tasks" replace />;

  if (isLoading) return <Loading />;

  const messagesSection = (
    <Messages>
      <MessagesTitle>Messages</MessagesTitle>
      {isFetchingMessages
        ? <Loading />
        : appointmentJourneyDetails && careJourneyDetails && (
          <MessageThreadDrawer
            patient={careJourneyDetails.clientProfile}
            careJourney={appointmentJourneyDetails}
            messageThreadSummary={messageThreadSummaries?.messageThreadSummaries[0] ?? newMessageThread}
            simple
            onNewMessageThread={setNewMessageThread}
          />
        )}
    </Messages>
  );

  const withLayout = (content: React.ReactNode) => (
    <Main>{content}</Main>
  );

  return (
    <>
      {careJourneyDetails && (
        <ClientDemographics
          patient={careJourneyDetails.clientProfile}
          isExpandable
          isElevated
          isDemographicsEditable={isDemographicEditAvailable}
          linkName
        />
      )}
      {appointmentJourneyDetails && (
        <Content>
          <TabsWrapper>
            <TabsComponent
              ref={tabsRef}
              tabsClassName={css({paddingLeft: `${sizes.medium} !important`})}
              tabs={[
                {
                  value: 0,
                  label: 'Progress',
                  content: withLayout(
                    <TaskEditProgress
                      ref={taskEditProgressRef}
                      fetching={isFetching}
                      careJourneyDetails={appointmentJourneyDetails as unknown as CareJourneyDetailsResponse}
                      invalidate={invalidate}
                      disabled={!canActOnTaskListForRoster}
                    />
                  ),
                },
                {
                  value: 1,
                  label: 'Log',
                  content: withLayout(
                    <TaskEditLog 
                      careJourneyDetails={appointmentJourneyDetails}
                      setCurrentTab={setCurrentTab} />
                  ),
                },
                {
                  value: 2,
                  label: 'Raw JSON',
                  content: <RawTaskJson careJourneyDetails={appointmentJourneyDetails} />,
                  hidden: !currentUser || !PermissionsHelpers.isBamboo(currentUser)
                },
              ]}
            />
          </TabsWrapper>
          {messagesSection}
        </Content>
      )}
      {showConfirmClose && (
        <ConfirmCloseModal
          close={() => setShowConfirmClose(false)}
          careJourneyDetails={appointmentJourneyDetails!}
          closeTaskEditDrawer={navigateBack}
          invalidate={invalidate}
        />
      )}
    </>
  );
    
};

export default BhAppointmentDetailPage;
